<template>
  <main class="status-page">
    <section class="status-hero">
      <div>
        <span class="eyebrow">AZLINUX STATUS</span>
        <h1>服务运行状态</h1>
        <p>中国站与香港站核心能力的实时状态和历史可用性。</p>
      </div>
      <button class="refresh-button" :disabled="loading" @click="loadAll">
        {{ loading ? "正在刷新…" : "刷新状态" }}
      </button>
    </section>

    <section class="overall" :class="`overall-${overallStatus}`">
      <span class="pulse"></span>
      <div>
        <strong>{{ overallText }}</strong>
        <small>最近检测：{{ latestUpdate }}</small>
      </div>
    </section>

    <section class="range-toolbar" aria-label="历史时间范围">
      <div>
        <strong>历史可用性</strong>
        <span>后端每 5 分钟采样，图表按天聚合</span>
      </div>
      <div class="range-buttons">
        <button v-for="option in rangeOptions" :key="option.days"
          :class="{ active: selectedDays === option.days }" @click="changeRange(option.days)">
          {{ option.label }}
        </button>
      </div>
    </section>

    <section class="site-stack">
      <article v-for="site in sites" :key="site.code" class="site-card">
        <header class="site-header">
          <div>
            <span class="site-code">{{ site.code === "hk" ? "HONG KONG" : "CHINA" }}</span>
            <h2>{{ site.name }}</h2>
          </div>
          <span class="status-badge" :class="statusClass(site.status)">{{ statusText(site.status) }}</span>
        </header>

        <div v-if="site.error" class="site-error">暂时无法获取该站点状态，我们会继续自动检测。</div>
        <div v-else class="service-list">
          <div v-for="component in mergedComponents(site)" :key="component.key" class="service-row">
            <div class="service-meta">
              <strong>{{ component.name }}</strong>
              <span class="current-state" :class="statusClass(component.currentStatus)">
                <i></i>{{ statusText(component.currentStatus) }}
              </span>
            </div>
            <div class="timeline-wrap">
              <div class="timeline" :style="timelineStyle">
                <span v-for="day in component.timeline" :key="day.date" class="day-block"
                  :class="`day-${day.status}`" :title="dayTooltip(day)"></span>
              </div>
              <div class="timeline-labels"><span>{{ rangeStartLabel }}</span><span>今天</span></div>
            </div>
            <div class="availability">
              <strong>{{ availabilityText(component.availability) }}</strong>
              <span>{{ selectedDays }} 天可用率</span>
            </div>
          </div>
        </div>
        <footer class="site-footer">
          <span>{{ formatTime(site.updated_at) }}</span>
          <span v-if="site.historyError">历史数据暂时不可用，实时检测不受影响</span>
        </footer>
      </article>
    </section>

    <section class="incident-panel">
      <div class="section-title">
        <div><span class="eyebrow">INCIDENTS</span><h2>最近事件</h2></div>
        <span>展示所选时间范围内的故障与恢复记录</span>
      </div>
      <div v-if="!incidents.length" class="empty-incidents">
        <span class="checkmark">✓</span>
        <div><strong>所选范围内没有故障事件</strong><p>核心服务保持稳定运行。</p></div>
      </div>
      <div v-else class="incident-list">
        <article v-for="incident in incidents" :key="`${incident.siteCode}-${incident.id}`">
          <span class="incident-dot" :class="incident.status === 'resolved' ? 'resolved' : 'open'"></span>
          <div>
            <strong>{{ incident.component_name }} · {{ incident.siteName }}</strong>
            <p>{{ incident.summary }}</p>
            <small>{{ formatTime(incident.started_at) }}<template v-if="incident.resolved_at"> — {{ formatTime(incident.resolved_at) }} 已恢复</template></small>
          </div>
          <span class="incident-badge" :class="incident.status">{{ incident.status === "resolved" ? "已恢复" : "处理中" }}</span>
        </article>
      </div>
    </section>

    <section class="status-note">
      <div class="legend">
        <span><i class="dot operational"></i>运行正常</span>
        <span><i class="dot partial_outage"></i>部分异常</span>
        <span><i class="dot major_outage"></i>严重故障</span>
        <span><i class="dot unknown"></i>暂无数据</span>
      </div>
      <p>可用率由有效的 5 分钟采样计算；暂无数据不会被计为正常。连续探测失败后才会确认故障，减少瞬时网络波动造成的误报。</p>
    </section>
  </main>
</template>

<script>
const SITE_ENDPOINTS = [
  { code: "cn", name: "中国站", url: "https://azlinux.com/api/status", historyUrl: "https://azlinux.com/api/status/history" },
  { code: "hk", name: "香港站", url: "https://hk.azlinux.com/api/status", historyUrl: "https://hk.azlinux.com/api/status/history" },
];

export default {
  name: "ServiceStatus",
  data() {
    return {
      sites: [], loading: false, timer: null, selectedDays: 30,
      rangeOptions: [{ days: 7, label: "最近 7 天" }, { days: 30, label: "最近 30 天" }, { days: 90, label: "最近 90 天" }],
    };
  },
  computed: {
    overallStatus() {
      if (!this.sites.length || this.sites.some(item => item.error)) return "partial_outage";
      if (this.sites.some(item => item.status === "major_outage")) return "major_outage";
      return this.sites.some(item => item.status !== "operational") ? "partial_outage" : "operational";
    },
    overallText() {
      return { operational: "所有核心服务运行正常", partial_outage: "部分服务出现异常", major_outage: "核心服务出现异常" }[this.overallStatus];
    },
    latestUpdate() {
      const values = this.sites.map(item => item.updated_at).filter(Boolean).sort();
      return values.length ? this.formatTime(values[values.length - 1]) : "等待首次检测";
    },
    timelineStyle() { return { gridTemplateColumns: `repeat(${this.selectedDays}, minmax(3px, 1fr))` }; },
    rangeStartLabel() {
      const date = new Date(); date.setDate(date.getDate() - this.selectedDays + 1);
      return `${date.getMonth() + 1}月${date.getDate()}日`;
    },
    incidents() {
      return this.sites.flatMap(site => (site.history?.incidents || []).map(item => ({ ...item, siteCode: site.code, siteName: site.name })))
        .sort((a, b) => new Date(b.started_at) - new Date(a.started_at)).slice(0, 12);
    },
  },
  mounted() { this.loadAll(); this.timer = window.setInterval(this.loadCurrent, 30000); },
  beforeUnmount() { window.clearInterval(this.timer); },
  methods: {
    async fetchJson(url) {
      const response = await fetch(url, { cache: "no-store" });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    },
    async loadAll() {
      this.loading = true;
      const results = await Promise.all(SITE_ENDPOINTS.map(async config => {
        const [current, history] = await Promise.allSettled([
          this.fetchJson(config.url), this.fetchJson(`${config.historyUrl}?days=${this.selectedDays}`),
        ]);
        if (current.status === "rejected") return { ...config, status: "major_outage", components: [], error: true };
        return { ...config, ...current.value, history: history.status === "fulfilled" ? history.value : null, historyError: history.status === "rejected" };
      }));
      this.sites = results;
      this.loading = false;
    },
    async loadCurrent() {
      const results = await Promise.allSettled(SITE_ENDPOINTS.map(config => this.fetchJson(config.url)));
      this.sites = this.sites.map((site, index) => results[index].status === "fulfilled"
        ? { ...site, ...results[index].value, error: false }
        : { ...site, error: true });
    },
    changeRange(days) { if (this.selectedDays !== days) { this.selectedDays = days; this.loadAll(); } },
    dateKeys() {
      const values = [];
      for (let offset = this.selectedDays - 1; offset >= 0; offset -= 1) {
        const date = new Date(); date.setHours(0, 0, 0, 0); date.setDate(date.getDate() - offset);
        values.push(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`);
      }
      return values;
    },
    mergedComponents(site) {
      const historyMap = Object.fromEntries((site.history?.components || []).map(item => [item.key, item]));
      const currentMap = Object.fromEntries((site.components || []).map(item => [item.key, item]));
      const keys = [...new Set([...Object.keys(currentMap), ...Object.keys(historyMap)])];
      return keys.map(key => {
        const history = historyMap[key] || {};
        const dayMap = Object.fromEntries((history.days || []).map(day => [day.date, day]));
        return {
          key, name: currentMap[key]?.name || history.name || key,
          currentStatus: currentMap[key]?.status || "unknown", availability: history.availability,
          timeline: this.dateKeys().map(date => dayMap[date] || { date, status: "unknown", availability: null, samples: 0 }),
        };
      });
    },
    availabilityText(value) { return value === null || value === undefined ? "—" : `${Number(value).toFixed(value >= 99 ? 2 : 1)}%`; },
    dayTooltip(day) {
      if (day.status === "unknown") return `${day.date} · 暂无数据`;
      return `${day.date} · 可用率 ${this.availabilityText(day.availability)} · ${day.samples} 个采样`;
    },
    statusClass(status) { return `state-${status || "unknown"}`; },
    statusText(status) {
      return { operational: "运行正常", partial_outage: "部分异常", major_outage: "核心异常", outage: "不可用", unknown: "暂无数据" }[status] || "未知";
    },
    formatTime(value) {
      if (!value) return "尚无检测时间";
      return new Intl.DateTimeFormat("zh-CN", { dateStyle: "medium", timeStyle: "medium", hour12: false }).format(new Date(value));
    },
  },
};
</script>

<style scoped>
.status-page{min-height:100vh;background:#f5f8fc;color:#172033;padding:42px clamp(16px,5vw,76px) 72px;text-align:left}.status-hero,.overall,.range-toolbar,.site-stack,.incident-panel,.status-note{max-width:1180px;margin-left:auto;margin-right:auto}.status-hero{display:flex;justify-content:space-between;align-items:flex-end;gap:28px}.eyebrow{color:#2563eb;font-size:.72rem;font-weight:800;letter-spacing:.16em}.status-hero h1{font-size:clamp(2rem,4vw,3.25rem);margin:10px 0 10px}.status-hero p{color:#64748b;margin:0}.refresh-button{border:1px solid #cbd5e1;background:#fff;color:#1e40af;border-radius:10px;padding:11px 18px;cursor:pointer}.refresh-button:disabled{opacity:.6}.overall{margin-top:28px;margin-bottom:18px;border-radius:16px;padding:19px 24px;display:flex;align-items:center;gap:14px;border:1px solid}.overall strong,.overall small{display:block}.overall small{margin-top:4px;opacity:.72}.overall-operational{background:#ecfdf5;border-color:#a7f3d0;color:#065f46}.overall-partial_outage{background:#fffbeb;border-color:#fde68a;color:#92400e}.overall-major_outage{background:#fef2f2;border-color:#fecaca;color:#991b1b}.pulse{width:11px;height:11px;border-radius:50%;background:currentColor;box-shadow:0 0 0 6px color-mix(in srgb,currentColor 14%,transparent)}.range-toolbar{display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;padding:4px 2px}.range-toolbar strong,.range-toolbar span{display:block}.range-toolbar span{color:#64748b;font-size:.8rem;margin-top:4px}.range-buttons{display:flex;background:#e8eef7;padding:4px;border-radius:10px}.range-buttons button{border:0;background:transparent;color:#64748b;padding:8px 13px;border-radius:7px;cursor:pointer}.range-buttons button.active{background:#fff;color:#1d4ed8;box-shadow:0 2px 8px #1e3a8a14}.site-stack{display:grid;gap:18px}.site-card,.incident-panel,.status-note{background:#fff;border:1px solid #e2e8f0;border-radius:18px;box-shadow:0 10px 35px #17203308}.site-card{padding:24px}.site-header{display:flex;justify-content:space-between;align-items:center;padding-bottom:18px;border-bottom:1px solid #eef2f7}.site-header h2{margin:4px 0 0;font-size:1.3rem}.site-code{font-size:.68rem;letter-spacing:.16em;color:#94a3b8;font-weight:700}.status-badge{font-size:.76rem;border-radius:999px;padding:6px 10px;background:#f1f5f9}.service-row{display:grid;grid-template-columns:180px minmax(260px,1fr) 105px;align-items:center;gap:20px;padding:17px 2px;border-bottom:1px solid #f1f5f9}.service-row:last-child{border-bottom:0}.service-meta strong{display:block;font-size:.92rem}.current-state{display:flex;align-items:center;gap:6px;font-size:.75rem;margin-top:5px}.current-state i,.dot{display:inline-block;width:7px;height:7px;border-radius:50%;background:currentColor}.timeline{display:grid;gap:3px;height:27px}.day-block{min-width:3px;border-radius:3px;background:#e5e7eb;transition:transform .15s,filter .15s}.day-block:hover{transform:scaleY(1.22);filter:saturate(1.2)}.day-operational{background:#20bf78}.day-partial_outage{background:#f59e0b}.day-major_outage,.day-outage{background:#ef4444}.day-unknown{background:#e2e8f0}.timeline-labels{display:flex;justify-content:space-between;color:#94a3b8;font-size:.67rem;margin-top:5px}.availability{text-align:right}.availability strong,.availability span{display:block}.availability strong{font-size:1.05rem;color:#0f766e}.availability span{font-size:.67rem;color:#94a3b8;margin-top:3px}.site-footer{display:flex;justify-content:space-between;gap:16px;color:#94a3b8;font-size:.74rem;padding-top:16px}.site-error{margin-top:18px;padding:18px;background:#fff7ed;color:#9a3412;border-radius:10px}.incident-panel{margin-top:18px;padding:24px}.section-title{display:flex;justify-content:space-between;align-items:flex-end;padding-bottom:18px;border-bottom:1px solid #eef2f7}.section-title h2{margin:5px 0 0;font-size:1.25rem}.section-title>span{color:#94a3b8;font-size:.78rem}.empty-incidents{display:flex;align-items:center;justify-content:center;gap:14px;padding:36px;color:#475569}.empty-incidents p{margin:4px 0 0;color:#94a3b8;font-size:.82rem}.checkmark{display:grid;place-items:center;width:34px;height:34px;border-radius:50%;background:#ecfdf5;color:#059669;font-weight:800}.incident-list article{display:grid;grid-template-columns:12px 1fr auto;gap:13px;align-items:start;padding:16px 2px;border-bottom:1px solid #f1f5f9}.incident-list article:last-child{border:0}.incident-dot{width:9px;height:9px;border-radius:50%;margin-top:5px}.incident-dot.resolved{background:#10b981}.incident-dot.open{background:#ef4444}.incident-list p{color:#64748b;margin:5px 0;font-size:.84rem}.incident-list small{color:#94a3b8}.incident-badge{font-size:.72rem;padding:5px 8px;border-radius:999px}.incident-badge.resolved{background:#ecfdf5;color:#047857}.incident-badge.open{background:#fef2f2;color:#b91c1c}.status-note{margin-top:18px;padding:20px 24px}.legend{display:flex;gap:22px;flex-wrap:wrap}.legend span{display:flex;align-items:center;gap:7px;color:#475569;font-size:.82rem}.status-note p{color:#64748b;font-size:.8rem;margin:13px 0 0;line-height:1.6}.state-operational,.operational{color:#059669}.state-partial_outage,.partial_outage{color:#d97706}.state-major_outage,.state-outage,.major_outage{color:#dc2626}.state-unknown,.unknown{color:#94a3b8}@media(max-width:800px){.status-hero{align-items:stretch;flex-direction:column}.refresh-button{width:100%}.range-toolbar{align-items:flex-start;flex-direction:column;gap:12px}.range-buttons{width:100%}.range-buttons button{flex:1}.site-card{padding:18px}.service-row{grid-template-columns:1fr 80px;gap:10px}.timeline-wrap{grid-column:1/-1;grid-row:2}.availability{grid-column:2;grid-row:1}.site-footer,.section-title{align-items:flex-start;flex-direction:column}.timeline{gap:2px}.section-title>span{line-height:1.5}}@media(max-width:420px){.status-page{padding-left:12px;padding-right:12px}.range-buttons button{font-size:.72rem;padding:8px 5px}.timeline{height:23px}.site-header h2{font-size:1.15rem}}
</style>
